import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Grid,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
} from '@mui/material';
import { FormikProps } from 'formik';
import { Delete, Star, StarOutline } from '@mui/icons-material';
import ConfirmationDialog from '../../../components/Dialog/ConfirmationDialog';
import { EncodingType, InstanceMutationType } from "../../../redux/api/api.types";
import Label from "../../../components/Label/Label";
import BasicTooltip from '../../../components/BasicTooltip';
import HelperText from "../../../components/HelperText/HelperText";
import { EncodingMethodsTypes, HelperTextVariant } from "../../../utils/enum/enum";
import EncodingTooltip from './EncodingTooltip';
import EncodingDialog from './EncodingDialog';
import { getFieldLabel } from './encodingFunctions';

type EncodingMethodsProps = {
  formik: FormikProps<InstanceMutationType>;
};

export default function EncodingMethods({ formik }: EncodingMethodsProps) {
  const [maxLimitMessage, setMaxLimitMessage] = useState("");
  const [encodingIndex, setEncodingIndex] = useState<number | undefined>(undefined);
  const [confirmationDialog, setConfirmationDialog] = useState<boolean>(false);
  const [pendingLookupEncoding, setPendingLookupEncoding] = useState<EncodingType | null>(null);


  const resetWarnings = () => {
    setMaxLimitMessage("");
  }

  const allEncodinglength = formik.values.encodings.length;

  useEffect(() => {
    if (allEncodinglength !== undefined) {
      if (allEncodinglength >= 4) {
        setMaxLimitMessage("Limit of maximum 4 encoding methods reached.");
      } else {
        resetWarnings();
      }
    }
  }, [allEncodinglength, formik.values.encodings]);

  const resetDefaultIfNeeded = (encodings: EncodingType[]): EncodingType[] => {
    const arrayCopy = [...encodings];
    if (!encodings.some((encoding) => encoding.default === true) && encodings.length > 0) {
      arrayCopy[0].default = true;
    };
    return arrayCopy;
  }

  const handleSettingDefault = (index: number) => {
    const arrayCopy = [...formik.values.encodings];
    formik.setFieldValue(
      "encodings",
      arrayCopy.map((encoding, encodingIndex) => ({
        ...encoding,
        default: encodingIndex === index
      }))
    );
  };

  const handleRemoveEncoding = (index: number) => {
    let newEncodings = formik.values.encodings.filter((_, encodingIndex) => index !== encodingIndex);
    newEncodings = resetDefaultIfNeeded(newEncodings);
    formik.setFieldValue(
      "encodings",
      newEncodings
    );
  };

  const handleEncodingDialog = (value: EncodingType | null) => {

    if (value === null) {
      setEncodingIndex(undefined);
      return;
    }

    if (value.method === EncodingMethodsTypes.LOOKUP) {
      setPendingLookupEncoding(value);
      setConfirmationDialog(true);
      setEncodingIndex(undefined);
      return;
    }

    if (encodingIndex !== undefined) {
      if (encodingIndex === -1) {
        formik.setFieldValue(
          "encodings",
          [...formik.values.encodings, value]
        );
      } else {
        let arrayCopy = [...formik.values.encodings];
        arrayCopy[encodingIndex] = value;
        arrayCopy = resetDefaultIfNeeded(arrayCopy);
        formik.setFieldValue(
          "encodings",
          arrayCopy
        )
      }
    }
    setEncodingIndex(undefined);
  }

  const handleConfirmLookup = () => {
    // Remove all encodings and add only lookup
    if (pendingLookupEncoding) {
      pendingLookupEncoding.default = true;
      formik.setFieldValue("encodings", [pendingLookupEncoding]);
    }
    setPendingLookupEncoding(null);
    setConfirmationDialog(false);
  };

  const handleCancelLookup = () => {
    setPendingLookupEncoding(null);
    setConfirmationDialog(false);
  };

  const disableEncodingDelete = formik.values.encodings.length < 2

  const getEncodingMethodLabel = (selectedEncodingType: EncodingType): string => {
    const { method, metadata } = selectedEncodingType;
    const metadataString = Object.entries(metadata).map(([key, value]) => `${getFieldLabel(key)}: ${value}`).join(', ')
    return `${method} ${metadataString}`;
  }

  const getEncodingItemKey = (selectedEncodingType: EncodingType): string => {
    const { method, metadata } = selectedEncodingType;
    const metadataString = Object.entries(metadata).map(([key, value]) => `${key}${value}`).join('')
    return `${method}${metadataString}`;
  }

  return (
    <Grid item xs={6} sx={{ pb: 2 }}>
      <Label text="Encoding methods" />
      <Box sx={{ display: "inline-block", verticalAlign: "middle", ml: 1 }}>
        <BasicTooltip text={<EncodingTooltip />} />
      </Box>
      <EncodingDialog
        index={encodingIndex}
        encodings={formik.values.encodings}
        handleClose={(value) => handleEncodingDialog(value)}
      />
      <Button
        onClick={() => setEncodingIndex(-1)}
        disabled={formik.values.encodings.some(encoding => encoding.method === EncodingMethodsTypes.LOOKUP)}
      >
        Add Encoding Method
      </Button>
      <List
        dense
      >
        {formik.values.encodings.map((encoding, index) => <ListItem
          key={`encoding_list_item_${getEncodingItemKey(encoding)}`}
          secondaryAction={
            <IconButton
              edge="end"
              aria-label="delete"
              disabled={disableEncodingDelete}
              onClick={() => handleRemoveEncoding(index)}
            >
              <Delete />
            </IconButton>
          }
        >
          <IconButton
            onClick={() => handleSettingDefault(index)}
          >
            {encoding.default ? <Star /> : <StarOutline />}
          </IconButton>
          <ListItemButton
            onClick={() => setEncodingIndex(index)}
          >
            <ListItemText
              primary={getEncodingMethodLabel(encoding)}
            />
          </ListItemButton>
        </ListItem>)}
      </List>
      {formik.errors.encodings && typeof formik.errors.encodings === 'string' && (
        <HelperText
          text={formik.errors.encodings as string}
        />
      )}

      {maxLimitMessage && (
        <HelperText
          text={maxLimitMessage}
          variant={HelperTextVariant.STANDARD}
        />
      )}
      {confirmationDialog && (
        <ConfirmationDialog
          isOpen={confirmationDialog}
          onClose={handleCancelLookup}
          onConfirm={handleConfirmLookup}
          title="Lookup Encoding Method"
          confirmText="Confirm"
          isWarning
        >
          When using LOOKUP, all the other encoding methods will be removed. Do you want to use only LOOKUP?
        </ConfirmationDialog>
      )}
    </Grid>
  );
}
